import { useEffect, useState } from "react";

import apiClient from "../../../services/apiClient";
import Modal from "../../../components/Modal";
import CloseButton from "../../../components/CloseButton";

// 수학 그리기·색칠 문항 채점(2026-10-26 대표). 서버: api/assignment/math_drawing.py
// 아이가 문제 그림 위에 그린 것을 해설 그림과 나란히 보고 맞음 · 틀림을 누른다. 누르면 점수를 다시 센다.
// 획 좌표는 그림 비율(0~1)이다(학생 MathDrawingPad.jsx 와 같은 모양).

const DRAWING_VIEW_WIDTH = 1000;
const BLANK_VIEW_HEIGHT = 600;

const DrawingView = ({ imageUrl, strokeList }) => {
  const [viewHeight, setViewHeight] = useState(BLANK_VIEW_HEIGHT);
  return (
    <div
      style={{
        position: "relative",
        width: imageUrl ? "fit-content" : "100%",
        maxWidth: "100%",
        border: "1px solid #E6EDEC",
        borderRadius: "12px",
        overflow: "hidden",
        background: "#fff",
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          style={{ display: "block", maxWidth: "100%", maxHeight: "360px" }}
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget;
            if (naturalWidth && naturalHeight) setViewHeight(Math.round((DRAWING_VIEW_WIDTH * naturalHeight) / naturalWidth));
          }}
        />
      ) : (
        <div style={{ width: "100%", aspectRatio: `${DRAWING_VIEW_WIDTH} / ${BLANK_VIEW_HEIGHT}` }} />
      )}
      <svg
        viewBox={`0 0 ${DRAWING_VIEW_WIDTH} ${viewHeight}`}
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      >
        {strokeList.map((stroke, index) => (
          <polyline
            key={index}
            points={(stroke.points || [])
              .map(([x, y]) => `${(x * DRAWING_VIEW_WIDTH).toFixed(1)},${(y * viewHeight).toFixed(1)}`)
              .join(" ")}
            fill="none"
            stroke={stroke.color}
            strokeWidth={stroke.width || 5}
            strokeOpacity={stroke.opacity ?? 1}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </svg>
    </div>
  );
};

const MARK_LIST = [
  { value: true, label: "맞음", color: "#00A396" },
  { value: false, label: "틀림", color: "#D63C5E" },
];

const MathDrawingModal = ({ assignmentId, onClose, onMarked }) => {
  const [data, setData] = useState(null);
  const [savingKey, setSavingKey] = useState("");

  useEffect(() => {
    apiClient
      .get("/api/v1/assignment/math/drawing/teacher", { params: { assignment_id: assignmentId }, withAuth: true })
      .then((response) => setData(response.data))
      .catch(() => setData({ error: true }));
  }, [assignmentId]);

  const mark = async (item, isCorrect) => {
    const key = `${item.section_id}:${item.problem_id}`;
    setSavingKey(key);
    try {
      const response = await apiClient.post(
        "/api/v1/assignment/math/drawing/teacher",
        { assignment_id: assignmentId, section_id: item.section_id, problem_id: item.problem_id, is_correct: isCorrect },
        { withAuth: true }
      );
      setData(response.data);
      if (onMarked) onMarked(response.data);
    } catch (error) {
      alert(error?.response?.data?.error || "저장하지 못했어요.");
    } finally {
      setSavingKey("");
    }
  };

  return (
    <Modal>
      <div
        style={{
          position: "relative",
          width: "min(960px, calc(100vw - 40px))",
          maxHeight: "86vh",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          borderRadius: "18px",
          overflow: "hidden",
        }}
      >
        <CloseButton onClick={onClose} />
        <div style={{ padding: "22px 22px 10px" }}>
          <div className="ws-modal-title">{data?.student_name} 그리기 채점</div>
          <p className="ws-modal-sub">{data?.title}</p>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 22px 22px" }}>
          {!data && <p className="ws-modal-sub">불러오는 중입니다.</p>}
          {data?.error && <p className="ws-modal-sub">불러오지 못했습니다.</p>}
          {(data?.item_list || []).map((item) => {
            const key = `${item.section_id}:${item.problem_id}`;
            return (
              <div key={key} style={{ padding: "16px 0", borderTop: "1px solid #E6EDEC" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <b style={{ fontSize: "15px" }}>
                    {item.type_name} {item.no}번
                  </b>
                  <span style={{ fontSize: "14px", color: "#5B6670", flex: 1 }}>{item.question}</span>
                  {item.is_submitted ? (
                    MARK_LIST.map((one) => (
                      <button
                        key={one.label}
                        type="button"
                        disabled={savingKey === key}
                        onClick={() => mark(item, one.value)}
                        style={{
                          padding: "8px 18px",
                          borderRadius: "999px",
                          border: `1.5px solid ${one.color}`,
                          background: item.is_correct === one.value ? one.color : "#fff",
                          color: item.is_correct === one.value ? "#fff" : one.color,
                          fontWeight: 800,
                          fontFamily: "inherit",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {one.label}
                      </button>
                    ))
                  ) : (
                    <span style={{ fontSize: "13px", color: "#9AA3AD", whiteSpace: "nowrap" }}>아직 안 냄</span>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "12px" }}>
                  <div>
                    <p className="ws-modal-sub" style={{ margin: "0 0 6px" }}>
                      학생이 그린 것{item.stroke_list.length ? "" : " (그리지 않음)"}
                    </p>
                    <DrawingView imageUrl={item.image_url} strokeList={item.stroke_list} />
                  </div>
                  <div>
                    <p className="ws-modal-sub" style={{ margin: "0 0 6px" }}>
                      정답 · 해설
                    </p>
                    {item.explanation_image_url && (
                      <img
                        src={item.explanation_image_url}
                        alt=""
                        style={{ display: "block", maxWidth: "100%", maxHeight: "360px", border: "1px solid #E6EDEC", borderRadius: "12px" }}
                      />
                    )}
                    <p style={{ fontSize: "14px", color: "#343A40", whiteSpace: "pre-wrap", margin: "8px 0 0" }}>
                      {item.answer}
                      {item.explanation ? `\n${item.explanation}` : ""}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default MathDrawingModal;
